import * as React from "react";
import { PercentageTextBoxEditor } from "../../../react-core/Html/PercentageTextBoxEditor";
import { MoneyTextBoxEditor } from "../../../react-core/Html/Money/MoneyTextBoxEditor";
import { CurrencyCode } from "../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ToggleButtonTwoOptions } from "../../../react-core/Html/ToggleButtonTwoOptions";
import { MoneyOrPercentageType } from "../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { Frequency } from "../../../Wrap/Constants/Enums/Domain/Frequency";
import { IBaseTextBoxEditorProps } from "../../../react-core/Html/BaseTextBoxEditor";
import { INumberTextBoxSpecificProps } from "../../../react-core/Html/NumberTextBox";
import { IProcessComponentProps, ProcessComponent } from "../Process/Process/ProcessComponent";
import { MoneyOrPercentage } from "../../../Wrap/Models/Models/Shared/MoneyOrPercentage";
import { MoneyHelper } from "../Common/MoneyHelper";
import * as classNames from "classNames";
import { MoneyDisplayHelper } from "../../../react-core/Helpers/MoneyDisplayHelper";

export interface IPercentageOrMoneyProps
    extends IBaseTextBoxEditorProps,
        INumberTextBoxSpecificProps,
        IProcessComponentProps {
    onChange: (changes) => void;
    data: MoneyOrPercentage;
    totalForEstimation?: number;
    onRef: (ref) => void;
    setDefaultValue?: boolean;
    valueLimit?: number;
}

export interface IPercentageOrMoneyState {}

export class PercentageOrMoney extends ProcessComponent<IPercentageOrMoneyProps, IPercentageOrMoneyState> {
    private _maxNumberOfDecimalPlaces = { errorMessage: `only allow 2 decimal places`, value: 2 };
    private _amountAtMost = { errorMessage: `cannot exceed 999999999 `, value: 999999999 };
    private _amountAtLeast = { errorMessage: `cannot less than 0`, value: 0 };

    constructor(props: IPercentageOrMoneyProps) {
        super(props);
        this.state = {};
    }

    getEstimatedValue() {
        const { data, totalForEstimation } = this.props;
        let amount = 0;
        if (data?.Type == MoneyOrPercentageType.Money) {
            amount = data?.Money?.Amount;
        } else {
            amount = (data?.Percentage * totalForEstimation) / 100;
        }
        
        amount = amount ? MoneyHelper.roundNumberToDecimalPlaces(amount, 2) : 0
        return MoneyDisplayHelper.FromatMoneyDisplay(amount);
    }

    initializeData(isAmount: boolean, defaultValue: any) {
        const data: MoneyOrPercentage = {
            Type: isAmount ? MoneyOrPercentageType.Money : MoneyOrPercentageType.Percentage,
            Percentage: defaultValue,
            Frequency: this.props.data?.Frequency || Frequency.Monthly,
            Money: { Amount: defaultValue, Currency: CurrencyCode.GBP },
        };
        this.props.onChange(data);
    }

    public render(): JSX.Element {
        const { data, label, infoText, required, tooltip } = this.props;
        const typeMoney = data?.Type == MoneyOrPercentageType.Money;
        const cssPercentageOrMoneyClasses = classNames("input-switcher", this.props.cssPercentageOrMoneyClasses);

        return (
            <fieldset>
                <div className={cssPercentageOrMoneyClasses}>
                    {typeMoney ? (
                        <MoneyTextBoxEditor
                            currency={CurrencyCode.GBP}
                            label={label}
                            required={required}
                            value={this.props.setDefaultValue ? 0 : data?.Money?.Amount}
                            tooltip={tooltip}
                            onChange={(v) => {
                                this.props.onChange({
                                    Money: { Amount: Number(v), Currency: CurrencyCode.GBP },
                                    Frequency: data?.Frequency || Frequency.Monthly,
                                });
                            }}
                            atLeast={this.props.atLeast || this._amountAtLeast}
                            atMost={this.props.atMost || this._amountAtMost}
                            validationRules={this.props.validationRules}
                            cssWrapper={this.props.cssWrapper}
                            disabled={this.props.disabled}
                        />
                    ) : (
                        <PercentageTextBoxEditor
                            value={this.props.setDefaultValue ? 0 : data?.Percentage}
                            label={label}
                            required={this.props.required}
                            maxNumberOfDecimalPlaces={
                                this.props.maxNumberOfDecimalPlaces || this._maxNumberOfDecimalPlaces
                            }
                            onChange={(v) => {
                                this.props.onChange({
                                    Percentage: v,
                                    Frequency: data?.Frequency || Frequency.Monthly,
                                    Type: MoneyOrPercentageType.Percentage,
                                });
                            }}
                            atLeast={this.props.atLeast || this._amountAtLeast}
                            valueLimit={this.props.valueLimit}
                            atMost={{ errorMessage: "cannot exceed 99.99% ", value: 99.99 }}
                            tooltip={this.props.tooltip}
                            infoText={infoText}
                            validationRules={this.props.validationRules}
                            cssWrapper={this.props.cssWrapper}
                            disabled={this.props.disabled}
                        />
                    )}
                    <ToggleButtonTwoOptions
                        optionOne="%"
                        optionTwo="£"
                        value={typeMoney}
                        onChange={(val) => {
                            this.initializeData(val, this.props.setDefaultValue ? 0 : null);
                        }}
                        disabled={this.props.disabled}
                    />
                    {this.props.totalForEstimation != null && (
                        <small>
                            <strong>Estimated </strong>£{this.getEstimatedValue()}
                        </small>
                    )}
                </div>
            </fieldset>
        );
    }
}
